import React, { createContext, useContext, useState, useCallback } from 'react'
import { theme } from '../../styles/theme'
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((type: ToastType, message: string, duration = 5000) => {
    const id = Math.random().toString(36).substring(7)
    setToasts(prev => [...prev, { id, type, message, duration }])

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }
  }, [])

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={{
        position: 'fixed',
        top: theme.spacing[6],
        right: theme.spacing[6],
        zIndex: theme.zIndex.toast,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[3],
        maxWidth: '400px',
      }}>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

interface ToastItemProps {
  toast: Toast
  onRemove: (id: string) => void
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [progress, setProgress] = React.useState(100)
  const [isExiting, setIsExiting] = React.useState(false)

  React.useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - (100 / (toast.duration! / 100))
          return newProgress < 0 ? 0 : newProgress
        })
      }, 100)
      return () => clearInterval(interval)
    }
  }, [toast.duration])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => onRemove(toast.id), 300)
  }

  const icons = {
    success: <CheckCircle size={20} />,
    error: <AlertCircle size={20} />,
    warning: <AlertTriangle size={20} />,
    info: <Info size={20} />,
  }

  const colors = {
    success: {
      bg: '#ECFDF5',
      border: theme.colors.success,
      text: '#065F46',
    },
    error: {
      bg: '#FEF2F2',
      border: theme.colors.error,
      text: '#991B1B',
    },
    warning: {
      bg: '#FFF7ED',
      border: theme.colors.warning,
      text: '#9A3412',
    },
    info: {
      bg: '#EFF6FF',
      border: theme.colors.info,
      text: '#1E40AF',
    },
  }

  const color = colors[toast.type]

  return (
    <div
      style={{
        backgroundColor: color.bg,
        border: `1px solid ${color.border}`,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing[4],
        boxShadow: theme.shadows.lg,
        display: 'flex',
        gap: theme.spacing[3],
        alignItems: 'flex-start',
        position: 'relative',
        overflow: 'hidden',
        transform: isExiting ? 'translateX(120%)' : 'translateX(0)',
        opacity: isExiting ? 0 : 1,
        transition: 'all 300ms ease-in-out',
        animation: 'slideInRight 300ms ease-out',
      }}
    >
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(120%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div style={{ color: color.border, flexShrink: 0 }}>
        {icons[toast.type]}
      </div>
      <div style={{ flex: 1, color: color.text, fontSize: theme.typography.fontSize.sm }}>
        {toast.message}
      </div>
      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: color.text,
          opacity: 0.6,
          transition: theme.transitions.fast,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
      >
        <X size={16} />
      </button>
      {toast.duration && toast.duration > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '3px',
            backgroundColor: color.border,
            width: `${progress}%`,
            transition: 'width 100ms linear',
          }}
        />
      )}
    </div>
  )
}
